/**
 * Template Preview Component
 * Modal for previewing template questions for all rater types
 */

import React, { useState, useEffect } from 'react';
import { X, Eye, User, Users, UserCheck, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const TemplatePreview = ({ 
  isOpen, 
  onClose, 
  templateId, 
  participantId = null 
}) => {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [activeTab, setActiveTab] = useState('SELF');

  const raterTabs = [
    { id: 'SELF', label: 'Self Assessment', icon: User, color: '#3b82f6' },
    { id: 'MANAGER', label: 'Manager', icon: UserCheck, color: '#8b5cf6' },
    { id: 'DIRECT_REPORT', label: 'Direct Report', icon: Users, color: '#10b981' },
    { id: 'ADHOC', label: 'Ad-hoc', icon: UserPlus, color: '#f59e0b' }
  ];

  useEffect(() => {
    if (isOpen && templateId) {
      loadPreview();
    }
  }, [isOpen, templateId, participantId]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = await getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL;
      
      // Build query params
      const params = new URLSearchParams();
      if (participantId) {
        params.append('participant_id', participantId);
      }

      const url = `${apiUrl}/leading/api/survey-templates/${templateId}/preview${params.toString() ? '?' + params.toString() : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load template preview');
      }

      const data = await response.json();
      setPreviewData(data.data || data);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const highlightParticipantName = (text, participantName) => {
    if (!participantName || !text) return text;
    
    // Split text by participant name and wrap matches
    const parts = text.split(new RegExp(`(${participantName})`, 'gi'));
    return parts.map((part, index) => {
      if (part.toLowerCase() === participantName.toLowerCase()) {
        return (
          <span 
            key={index}
            style={{
              background: '#fef3c7',
              padding: '0.125rem 0.25rem',
              borderRadius: '0.25rem',
              fontWeight: '600',
              color: '#92400e'
            }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Eye size={24} color="#10b981" />
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', margin: 0 }}>
                Template Preview
              </h2>
              {previewData && (
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                  {previewData.template?.name} • Participant: {previewData.participant_name}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '0.375rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '4rem'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #e5e7eb',
                  borderTop: '4px solid #10b981',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem'
                }}></div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading preview...</p>
                <style>{`
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            </div>
          ) : error ? (
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '4rem'
            }}>
              <div style={{
                padding: '2rem',
                background: '#fef2f2',
                border: '2px solid #dc2626',
                borderRadius: '0.5rem',
                textAlign: 'center',
                maxWidth: '400px'
              }}>
                <p style={{ 
                  color: '#991b1b', 
                  fontSize: '0.875rem', 
                  fontWeight: '600',
                  margin: '0 0 0.5rem 0'
                }}>
                  Failed to load preview
                </p>
                <p style={{ color: '#991b1b', fontSize: '0.75rem', margin: 0 }}>
                  {error}
                </p>
              </div>
            </div>
          ) : previewData ? (
            <>
              {/* Tabs */}
              <div style={{
                display: 'flex',
                borderBottom: '2px solid #e5e7eb',
                padding: '0 1.5rem',
                gap: '0.5rem',
                flexShrink: 0,
                overflowX: 'auto'
              }}>
                {raterTabs.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const questionCount = previewData.questions_by_rater?.[tab.id]?.length || 0;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        padding: '1rem 1.5rem',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: `3px solid ${isActive ? tab.color : 'transparent'}`,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: isActive ? tab.color : '#6b7280',
                        fontWeight: isActive ? '600' : '500',
                        fontSize: '0.875rem',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Icon size={16} />
                      <span>{tab.label}</span>
                      <span style={{
                        background: isActive ? tab.color : '#e5e7eb',
                        color: isActive ? 'white' : '#6b7280',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {questionCount}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Questions List */}
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: '1.5rem'
              }}>
                {previewData.questions_by_rater?.[activeTab]?.length > 0 ? (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {previewData.questions_by_rater[activeTab].map((question, index) => (
                      <div
                        key={question.id || index}
                        style={{
                          padding: '1.25rem',
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '1rem'
                        }}>
                          <div style={{
                            flexShrink: 0,
                            width: '32px',
                            height: '32px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: '700'
                          }}>
                            {index + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{
                              fontSize: '0.875rem',
                              color: '#111827',
                              lineHeight: '1.6',
                              margin: 0
                            }}>
                              {highlightParticipantName(question.text, previewData.participant_name)}
                            </p>
                            {question.competency_id && (
                              <div style={{
                                marginTop: '0.75rem',
                                fontSize: '0.75rem',
                                color: '#6b7280'
                              }}>
                                <span style={{ fontWeight: '600' }}>Competency:</span> {question.competency_id}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '4rem 2rem',
                    textAlign: 'center',
                    color: '#9ca3af'
                  }}>
                    <p style={{ fontSize: '0.875rem', margin: 0 }}>
                      No questions available for this rater type
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;
